import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Container, Grid, Typography, Box, Button, Card, CardContent, Toolbar } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InsightsIcon from '@mui/icons-material/Insights';
import SecurityIcon from '@mui/icons-material/Security';
import Logo from '../components/Logo';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PetsIcon from '@mui/icons-material/Pets';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PublicIcon from '@mui/icons-material/Public';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const features = [
  {
    title: 'Armed Security',
    description: 'Highly trained, licensed armed security officers for maximum protection and deterrence.',
    icon: <SecurityIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Residential Security',
    description: 'Protect your home and loved ones with 24/7 professional security patrols and monitoring.',
    icon: <HomeIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Retail Security',
    description: 'Prevent theft and protect assets with specialized retail security solutions.',
    icon: <StorefrontIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Government Protection',
    description: 'Secure government facilities with cleared personnel and advanced protocols.',
    icon: <AccountBalanceIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'K-9 Units',
    description: 'Elite dog units for enhanced detection and security capabilities.',
    icon: <PetsIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Mobile Patrols',
    description: 'Regular patrol services with GPS-tracked vehicles and rapid response.',
    icon: <DirectionsCarIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
];

const benefits = [
  {
    title: 'Rapid Deployment',
    description: 'Get professional security personnel on-site within hours, not days.',
    icon: <FlashOnIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Elite Training',
    description: 'All officers undergo rigorous training and continuous professional development.',
    icon: <MilitaryTechIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
  {
    title: 'Complete Coverage',
    description: 'Nationwide service with local expertise and 24/7 availability.',
    icon: <PublicIcon sx={{ fontSize: 40, color: '#2563eb' }} />,
  },
];

const Home = () => {
  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Add toolbar spacing for fixed header */}
      <Toolbar />
      
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          mt: -8, // Compensate for toolbar spacing
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                <Logo variant="light" />
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  mb: 3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(to right, #ffffff, #e2e8f0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                America's Premier Security Services Provider
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontWeight: 400,
                  maxWidth: '800px',
                  mx: 'auto',
                  opacity: 0.9,
                  lineHeight: 1.6,
                }}
              >
                Professional Armed and Unarmed Security Solutions for Government, Commercial, and Residential Clients
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: '#2563eb',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: '50px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  Request a Quote
                </Button>
                <Button
                  component={Link}
                  to="/contact"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: '50px',
                    borderWidth: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.9)',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  Contact Sales
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 150%, rgba(99,102,241,0.4) 0%, transparent 50%)',
          }}
        />
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{ mb: 6, fontWeight: 700, color: 'text.primary' }}
          >
            Our Security Services
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 20px -8px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{ mb: 6, fontWeight: 700, color: 'text.primary' }}
          >
            Why Choose SecurityOnDemand
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 20px -8px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: 'background.default',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
            Ready to Enhance Your Security?
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
            Get a customized security solution tailored to your needs.
          </Typography>
          <Button
            component={Link}
            to="/register"
            variant="contained"
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '50px',
              background: 'linear-gradient(45deg, #2563eb 30%, #3b82f6 90%)',
              boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1d4ed8 30%, #2563eb 90%)',
                boxShadow: '0 6px 8px rgba(37, 99, 235, 0.3)',
              },
            }}
          >
            Request a Quote Now
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 